import { useState, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import axiosClient from "../api/axiosClient";
import { useNavigate, Link } from "react-router-dom";

// MUI imports
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd
} from "@mui/icons-material";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const submit = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axiosClient.post("api/auth/login", { email, password });

      login(res.data.token, res.data.user); // store token + user
      navigate("/");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      submit();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        mt: 8,
        minHeight: "80vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Paper 
        elevation={6} 
        sx={{ 
          p: 4, 
          width: "100%",
          // background: "linear-gradient(135deg, #3E1E68 0%, #5D2F77 100%)",
          background:"#2a1c32ff",
          color: "white",
          borderRadius: 3
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 2}}>
          <LoginIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Sign in to access your notes
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.1)",
              color: "white"
            }}
          >
            {error}
          </Alert>
        )}
{/* 
        Login Form */}


        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ color: "white",}}>
              Email
            </Typography>
          <TextField
            // label="Email"
            type="email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            required
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: "white" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#861515ff"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ffffff",
                  // transform: "translate(14px, -9px) scale(0.75)"
                }
              },
              "& .MuiInputLabel-root": {
                color: "white",
                // marginBottom: 581, // Adds space below the label
                // transform: "translate(14px, 20px) scale(1)",
                // transform: "translate(14px, 12px) scale(1)",
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "white"
              }
            }}
          />
<Typography variant="subtitle2" sx={{ color: "white",}}>
    Password
  </Typography>
          <TextField
            // label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={handleKeyPress}
            required
            fullWidth
            
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "white" }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    sx={{ color: "white" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
                
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#ffffff"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#ffffff"
                }
              },
              "& .MuiInputLabel-root": {
                color: "white",
                

              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "white"
              },
               
            }}
          />






          {/* Login Button */}
          <Button
            variant="contained"
            onClick={submit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
            size="large"
            sx={{
              backgroundColor: "white",
              color: "#3E1E68",
              py: 1.5,
              fontWeight: "bold",
              fontSize: "1.1rem",
              borderRadius: 2,
              mt: 2,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                transform: "translateY(-2px)",
                boxShadow: 3
              },
              "&:disabled": {
                backgroundColor: "rgba(255,255,255,0.5)"
              }
            }}
          >
            {loading ? "Signing In..." : "Login"}
          </Button>

          <Divider sx={{ backgroundColor: "rgba(255,255,255,0.3)", my: 2 }} />

          {/* Register Link */}
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
              Don't have an account?
            </Typography>
            <Button
              component={Link}
              to="/register"
              variant="outlined"
              startIcon={<PersonAdd />}
              sx={{
                color: "white",
                borderColor: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)"
                }
              }}
            >
              Create Account
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}